import * as React from "react";

interface SwitchProps {
  label: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  yesLabel?: string;
  noLabel?: string;
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ label, checked = false, onCheckedChange, disabled = false, id, name, yesLabel = "Yes", noLabel = "No" }, ref) => {
    const [isChecked, setIsChecked] = React.useState(checked);

    React.useEffect(() => {
      setIsChecked(checked);
    }, [checked]);

    const handleToggle = () => {
      if (disabled) return;
      const newValue = !isChecked;
      setIsChecked(newValue);
      onCheckedChange?.(newValue);
    };

    return (
      <div className="flex flex-col gap-2">
        <label htmlFor={id} className="text-sm font-medium text-gray-900 dark:text-gray-300 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">
          {label}
        </label>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 colorful:text-gray-600 yellow:text-[#92400e] pink:text-[#9f1239] orange:text-[#9a3412] blue:text-[#1e40af] red:text-[#991b1b] green:text-[#166534]">
            {noLabel}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={isChecked}
            disabled={disabled}
            onClick={handleToggle}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              focus:outline-none focus:ring-2 focus:ring-offset-2
              ${isChecked 
                ? 'bg-blue-600 dark:bg-blue-500 colorful:bg-purple-600 yellow:bg-[#fbb615] pink:bg-[#df1b82] orange:bg-[#f36f26] blue:bg-[#057dbb] red:bg-[#c22034] green:bg-[#3ead49] focus:ring-blue-500 colorful:focus:ring-purple-500 yellow:focus:ring-[#fbb615] pink:focus:ring-[#df1b82] orange:focus:ring-[#f36f26] blue:focus:ring-[#057dbb] red:focus:ring-[#c22034] green:focus:ring-[#3ead49]' 
                : 'bg-gray-300 dark:bg-gray-600 colorful:bg-gray-300 yellow:bg-gray-300 pink:bg-gray-300 orange:bg-gray-300 blue:bg-gray-300 red:bg-gray-300 green:bg-gray-300 focus:ring-gray-400'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform
                ${isChecked ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 colorful:text-gray-600 yellow:text-[#92400e] pink:text-[#9f1239] orange:text-[#9a3412] blue:text-[#1e40af] red:text-[#991b1b] green:text-[#166534] min-w-[2rem]">
            {yesLabel}
          </span>
          <input
            ref={ref}
            type="checkbox"
            id={id}
            name={name}
            checked={isChecked}
            onChange={(e) => {
              setIsChecked(e.target.checked);
              onCheckedChange?.(e.target.checked);
            }}
            className="sr-only"
            disabled={disabled}
          />
        </div>
      </div>
    );
  }
);

Switch.displayName = "Switch";

export { Switch };
